import React, { useEffect, useState } from "react"
import {
    containsNonEmptyInputData,
    FileMap,
    InputField,
    isInputRequired,
    isRequiredInputDataPresent,
    NodeInputMap,
    Node,
    richToPlain,
    Run,
    isInputNode, isModelNode, isDisplayNode, isChatNode,
    ChatMessageData
} from "api-sdk"
import { ComponentProps, FileUrlProvider, MenuControls, ModelNodeSteps } from "api-sdk-react"
import { ActionButton, InfoBox, NestedModelPortalControls, ReaderConfig, useMessages } from "reader-common"
import { UserMessage } from "../components/chat/userMessage";
import { AnimationInfo, AnimationType } from "../reader";
import { ChatStyleConfig } from "../types";
import { ChatNodeInput } from "./chat/chatNodeInput"

export type ChatState = {
    chatMessageDraft: string,
    pendingChatMessage?: string,
    isActionDismissed: boolean
}
export interface InputContainerProps extends ComponentProps {
    inputMap: NodeInputMap,
    fileMap: FileMap,
    setInputMap: (map: NodeInputMap) => void,
    renderInputFields: (fields: InputField[]) => { [inputField: string]: React.JSX.Element },
    animateToMessageAtHistoryIdx: number,
    animationInfo: AnimationInfo,

    run: Run,
    node: Node,
    config: ReaderConfig,
    fileUrlProvider: FileUrlProvider,
    styleConfig: ChatStyleConfig,
    menu: MenuControls,
    onSubmit: () => void,
    onSendChatMessage: (message: ChatMessageData) => void,
    nestedModelPortalControls: NestedModelPortalControls

    animateLeave?: boolean,
    isLoading?: boolean,
}

export function InputContainer(props: InputContainerProps) {

    const node = props.node

    const config = props.config
    const [ chatState, setChatState ] = useState({ chatMessageDraft: "", pendingChatMessage: "", isActionDismissed: false } as ChatState)
    const Msg = useMessages()

    let inputContent: React.JSX.Element   = undefined
    let submitButton: React.JSX.Element   = undefined
    let waitingInfo: React.JSX.Element  = undefined

    const onSubmit = props.onSubmit

    // Reset chat message text when node changes or chat history changes
    useEffect(() => {
        setChatState({ chatMessageDraft: "", isActionDismissed: false })
    }, [ node, props.run.state.history.path ])

    // Use keyboard shortcut
    const { inputMap, fileMap, onSendChatMessage } = props
    useEffect(() => {
        const submitShortcutListener = (e: KeyboardEvent) => {
            const canSubmit = (node.nodeType === "INPUT" && isRequiredInputDataPresent(node, inputMap, fileMap))
                || (node.nodeType === "PASSIVE")
                || (node.nodeType === "CHAT" && chatState.chatMessageDraft.length > 0)
            if ((e.ctrlKey || e.shiftKey) && e.key === "Enter" && canSubmit) {
                if (node.nodeType === "CHAT") {
                    onSendChatMessage({ message: chatState.chatMessageDraft })
                } else {
                    onSubmit()
                }
            }
        }
        window.addEventListener("keydown", submitShortcutListener)
        return () => window.removeEventListener("keydown", submitShortcutListener)
    }, [ inputMap, fileMap, node, onSubmit, chatState, onSendChatMessage ])

    /** Render input field with input component and label, annotation etc. */
    function renderInputField(field: InputField, inputElement: React.JSX.Element, includeDivider: boolean)
    {
        const divider = includeDivider ? (
            <div className="divider" />
        ) : null

        const showInputLabel = ![ "TEXT", "NUMBER" ].includes(field.valueType)

        const annotation = field.annotation
        const nodeInputLabel = field.label

        let inputLabel: React.JSX.Element = undefined

        if ((nodeInputLabel && showInputLabel) || annotation)
        {
            const annotationButton = annotation ? (
                <ActionButton
                    className="fade annotation-button"
                    text={ props.config.messages.help }
                    iconPosition="start"
                    iconName="help"
                    onClick={ e => props.menu.showMenu({
                        anchor: e.currentTarget,
                        render: () => (
                            <InfoBox
                                onClose={ props.menu.dismissMenu }
                                text={ annotation }
                                config={ props.config }
                            />
                        )
                    }) }
                />
            ) : (<div/>)

            inputLabel = showInputLabel ? (
                <div className="input-detail">
                    <div className="input-label">
                        { nodeInputLabel || "" }
                    </div>
                    { annotationButton }
                </div>
            ) : (
                <div>{ annotationButton }</div>
            )
        }

        return (
            <div className="input-section" key={ "field-" + field.id } role="region">
                { divider }
                { inputLabel }
                { inputElement }
            </div>
        )
    }

    const shouldAnimateToMessage = props.animateToMessageAtHistoryIdx == props.run.state.history.path.length - 1

    if (!props.run.state.waitingState) {

        let submitButtonText = (isDisplayNode(node) && !node.isEndNode)
            ? config.messages.passiveAnswer
            : config.messages.next
        let submitFn = onSubmit
        let canSubmit: boolean = false

        if (!isChatNode(node)) {
            // Show input / passive node
            const inputFields = isInputNode(node) ? node.inputFields : [ ]
            canSubmit = !isInputNode(node) || isRequiredInputDataPresent(node, props.inputMap, props.fileMap)

            const renderedInputFieldComponents = props.renderInputFields(inputFields)
            inputContent = (
                <div
                    key={ "input:" + node?.id }
                    className={ "input-content " + (shouldAnimateToMessage ? " animate-to-message" : "") + (props.animateLeave ? " animate-leave" : "") }>
                    { inputFields.map((field, idx) => renderInputField(field, renderedInputFieldComponents[String(field.id)], idx > 0)) }
                </div>
            )

            // When multiple choice is present, treat "EMPTY"-input and empty "MULTIPLE_CHOICE"-input the same in UI
            if (isInputNode(node) && !isInputRequired(node) && !containsNonEmptyInputData(node, props.inputMap, props.fileMap)) {
                submitButtonText = config.messages.skip
            }
        }
        else if (isChatNode(node)) {
            const lastMessage = node.messages[node.messages.length - 1]
            const isSystemMessage = lastMessage?.type == "SYSTEM"

            canSubmit = chatState.chatMessageDraft.length > 0 && (!isSystemMessage || chatState.isActionDismissed === true || lastMessage.action == null)
            submitFn = () => props.onSendChatMessage({ message: chatState.chatMessageDraft })
            submitButtonText = config.messages.sendChatMessage

            inputContent = (
                <div className={ "input-content " + (shouldAnimateToMessage ? " animate-to-message" : "") + (props.animateLeave ? " animate-leave" : "") }>
                    <ChatNodeInput
                        chatState={ chatState }
                        setChatState={ setChatState }
                        autoFocus={ true }
                        config={ config }
                        fileUrlProvider={ props.fileUrlProvider }
                        action={ lastMessage?.type === "SYSTEM" ? lastMessage.action : undefined }
                        onSendChatMessage={ props.onSendChatMessage }
                    />
                </div>
            )
        }

        submitButton = (
            <ActionButton
                onClick={ submitFn }
                iconName="chevron_right"
                className="continue-button"
                text={ submitButtonText }
            />
        )

        if (props.isLoading || shouldAnimateToMessage || !node)
        {
            submitButton = (
                <ActionButton
                    disabled
                    loading
                    className="continue-button"
                />
            )
        }
        else if (!canSubmit)
        {
            submitButton = (
                <ActionButton
                    disabled
                    className="continue-button"
                    iconName="chevron_right"
                    text={ submitButtonText }
                />
            )
        }
    } else {
        // If we have a model node & it's configured to show the link, show it
        if (node && isModelNode(node) && node.nestedRunUrl) {
            submitButton = (
                <ActionButton
                    className="continue-button"
                    iconName="chevron_right"
                    text={ richToPlain(node.linkLabel) || config.messages.next }
                    onClick={ () => props.nestedModelPortalControls.showNestedRun(node.nestedRunUrl) }
                />
            )
        }

        const waitingState = props.run.state.waitingState

        // If we have a model node & it's configured to show the steps, show them
        if (node && isModelNode(node) && node.dispatchSteps?.length > 0) {
            waitingInfo = (
                <ModelNodeSteps node={ node } isFinished={ false } />
            )
        } else {
            const waitIndicator = (
                <div className='waiting-indicator'><div className='inner'></div></div>
            )

            waitingInfo = (waitingState.message || waitingState.description) ? (
                <div className="waiting-info-box">
                    <div className="texts">
                        <div className="message">{ waitingState.message ?? Msg.state.waiting }</div>
                        <div className="description">{ waitingState.description ?? "" }</div>
                    </div>
                    { !waitingState.isWaitingForUserAction && waitIndicator }
                </div>
            ) : waitIndicator
        }
    }

    let messageAnimationProp = null

    if (shouldAnimateToMessage) {
        const isPassive = props.animationInfo.type === AnimationType.PASSIVE

        messageAnimationProp = node ? (
            <UserMessage
                runState={ props.run.state }
                node={ node }
                nodeInputMap={ props.inputMap }
                fileMap={ props.fileMap }
                chatMessageDraft={ chatState.pendingChatMessage }
                config={ config }
                styleConfig={ props.styleConfig }
                className={ (isPassive ? "passive " : "") + "message-animation-prop" }
                onEdit={ isPassive ? undefined : () => { /* onEdit specified just so that the edit button appears */ } }
                canEdit={ !isPassive }
            />
        ) : null
    }

    return (
        <div className="input-container">
            { messageAnimationProp }
            { inputContent }
            { waitingInfo }
            <div className="button-row">
                { submitButton }
            </div>
        </div>
    )

}
